import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { translateServerError, translateServerMessage } from './i18n-core';
import { consultationFeeReasonFrom } from './fee-gate-reasons';
import type { IssuedTokens, ModuleCode, SessionUser } from '@his/shared';
import { ApiError, get, migrateLegacySession, post, session, sessionHeaders } from './api';
import { adoptSessionDisplay } from './utils';
import { bindBranchUser, ensureBranchSelected } from './branch';
import { applyBrandColor } from './branding';

interface AuthState {
  user: SessionUser | null;
  loading: boolean;
  /** True while the user holds an enrolment-only token and must finish 2FA. */
  needsTwoFactorSetup: boolean;
  /** Signed in on a temporary password: the shell sends them to /change-password. */
  mustChangePassword: boolean;
  signIn: (tenant: string, identifier: string, password: string, totp?: string) => Promise<SessionUser>;
  signInPlatform: (email: string, password: string, totp?: string) => Promise<SessionUser>;
  /**
   * Owner-portal sign-in (LIMS-parity): email + TOTP, no password. Returns an
   * enrolment challenge on the owner's very first sign-in, a session otherwise.
   */
  signInOwner: (
    email: string,
    totp?: string,
    enrolmentCode?: string,
  ) => Promise<{ enrollment: { secret: string; otpauthUrl: string } } | { user: SessionUser }>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
  can: (...permissions: string[]) => boolean;
  canWrite: boolean;
  isSuperAdmin: boolean;
  hasModule: (module: ModuleCode) => boolean;
  isRetired: (module: ModuleCode) => boolean;
  worksIn: (module: ModuleCode) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const setUser = useCallback((next: SessionUser | null) => {
    bindBranchUser(next);
    ensureBranchSelected(next);
    applyBrandColor(next && !next.isPlatformUser ? next.primaryColor ?? null : null);
    adoptSessionDisplay(next, next ? session.branch() : null);
    setUserState(next);
  }, []);

  const refreshUser = useCallback(async () => {
    await migrateLegacySession();
    if (!session.access()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      setUser(await get<SessionUser>('/auth/me'));
    } catch {
      session.clear();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshUser();
    // The API client raises this when a refresh fails, so an expired session
    // clears the UI instead of leaving a signed-in shell that 401s everywhere.
    const onSignedOut = () => setUser(null);
    window.addEventListener('his:signed-out', onSignedOut);
    return () => window.removeEventListener('his:signed-out', onSignedOut);
  }, [refreshUser]);

  const signIn = useCallback(
    async (tenant: string, identifier: string, password: string, totp?: string) => {
      session.setTenant(tenant.trim().toLowerCase());
      const result = await post<{ tokens: IssuedTokens; user: SessionUser }>('/auth/login', {
        identifier,
        password,
        ...(totp ? { totp } : {}),
      });
      session.setTokens(result.tokens);
      setUser(result.user);
      return result.user;
    },
    [],
  );

  const signInPlatform = useCallback(async (email: string, password: string, totp?: string) => {
    session.setTenant(null);
    const result = await post<{ tokens: IssuedTokens; user: SessionUser }>('/auth/platform/login', {
      email,
      password,
      ...(totp ? { totp } : {}),
    });
    session.setTokens(result.tokens);
    setUser(result.user);
    return result.user;
  }, []);

  const signInOwner = useCallback(async (email: string, totp?: string, enrolmentCode?: string) => {
    session.setTenant(null);
    const result = await post<
      { enrollment: { secret: string; otpauthUrl: string } } | { tokens: IssuedTokens; user: SessionUser }
    >('/auth/platform/owner-login', {
      email,
      ...(totp ? { totp } : {}),
      ...(enrolmentCode && !totp ? { enrolmentCode } : {}),
    });
    if ('enrollment' in result) return { enrollment: result.enrollment };
    session.setTokens(result.tokens);
    setUser(result.user);
    return { user: result.user };
  }, []);

  const signOut = useCallback(async () => {
    if (session.impersonating()) {
      await post('/auth/impersonation/end').catch(() => undefined);
      session.clear();
      setUser(null);
      return;
    }
    const legacy = session.legacyRefresh();
    try {
      await post('/auth/logout', legacy ? { refreshToken: legacy } : {}, { headers: sessionHeaders() });
    } catch {
      // Signing out must succeed locally even if the server is unreachable.
    }
    session.clear();
    setUser(null);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      needsTwoFactorSetup: Boolean(user && user.requires2fa && !user.has2fa),
      mustChangePassword: Boolean(user?.mustChangePassword),
      signIn,
      signInPlatform,
      signInOwner,
      signOut,
      refreshUser,
      /**
       * Permission check used by both navigation and individual controls.
       * The server checks again on every request — this only decides what to
       * show, so a stale cache can never grant access, only hide something.
       */
      can: (...permissions: string[]) => {
        if (!user) return false;
        if (user.permissions.includes('*')) return true;
        return permissions.some((p) => user.permissions.includes(p));
      },
      canWrite: Boolean(user) && !user?.readOnlyReason && (user?.lockState ?? 'NONE') === 'NONE',
      isSuperAdmin: Boolean(user?.isSuperAdmin),
      hasModule: (module: ModuleCode) => Boolean(user?.modules.includes(module)),
      isRetired: (module: ModuleCode) => Boolean(user?.retiredModules?.includes(module)),
      worksIn: (module: ModuleCode) =>
        Boolean(user?.modules.includes(module)) && (!user?.roleModules || user.roleModules.includes(module)),
    }),
    [user, loading, signIn, signInPlatform, signInOwner, signOut, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}

/** Turns an API error into a message a desk clerk can act on. */
export function describeError(error: unknown): string {
  if (error instanceof ApiError) {
    const feeReason = consultationFeeReasonFrom(error.details);
    if (feeReason) return `${translateServerMessage(error.message) ?? error.message}: ${feeReason}`;
    const translated = translateServerError(error.localised?.messageKey, error.localised?.params);
    if (translated) return translated;
    const plain = error.code === 'validation_failed' && error.details.length ? null : translateServerMessage(error.message);
    if (plain) return plain;
    if (error.details.length && error.code === 'validation_failed') {
      if (error.details.every((d) => error.message.includes(d.message))) return translateServerMessage(error.message) ?? error.message;
      return `${error.message}: ${error.details.map((d) => `${d.path ?? ''} ${d.message}`.trim()).join(', ')}`;
    }

    // 429 and 423 arrive looking almost identical and the raw server text for
    // each reads the same to a receptionist — "try again later". They have
    // opposite causes and opposite fixes, so name which one it is: a 429 is
    // the CONNECTION (everyone on this network is affected, the account is
    // fine), a 423 is this ACCOUNT alone.
    const retryAfter = Number(
      error.details.find((d) => d.path === 'retryAfterSeconds')?.message ?? '',
    );
    const minutes = Number.isFinite(retryAfter) && retryAfter > 0
      ? Math.max(1, Math.ceil(retryAfter / 60))
      : null;
    const inAbout = minutes ? `about ${minutes} minute${minutes === 1 ? '' : 's'}` : 'a few minutes';

    if (error.status === 429 || error.code === 'RATE_LIMITED') {
      return `Too many sign-in attempts from this hospital's internet connection. Please wait ${inAbout} and try again — your account is fine and nothing has been lost.`;
    }
    if (error.code === 'ACCESS_BLOCKED') {
      return `Your access has been temporarily suspended by an administrator. It lifts in ${inAbout} — contact your administrator if you need it sooner.`;
    }

    return error.message;
  }
  if (error instanceof Error) return error.message;
  return 'Something went wrong';
}
